/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import { SortDirection } from "~/components/SortSelector";
import { PageName } from "./constants";
import { SortByOrder, SortByValue, SortKeysUsers } from "./types";

type Props = {
  field: SortByValue;
  order: SortByOrder;
  page: number;
  pageName: PageName;
  updateQueryState: (params: SortKeysUsers & { page: string }) => void;
};

export function useSettingsSort({
  field,
  order,
  page,
  pageName,
  updateQueryState,
}: Props) {
  const defaultValue =
    pageName === PageName.UsersAndPermissions
      ? SortByValue.UserEmailId
      : SortByValue.CreationDate;
  const [sortField, setSortField] = useState<SortByValue>(
    field ?? defaultValue
  );

  useEffect(() => {
    setSortField(field ?? defaultValue);
  }, [defaultValue, field]);

  const onSortSelect = (filter: SortByValue) => {
    const isDefaultDescending = order === SortByOrder.Descending;
    updateQueryState({
      field: filter,
      order: isDefaultDescending
        ? SortByOrder.Descending
        : SortByOrder.Ascending,
      page: page.toString(),
    });
  };

  const onSortOrderChange = (directionValue: SortDirection) => {
    const sortOrderDirection =
      directionValue === SortDirection.Ascending
        ? SortByOrder.Ascending
        : SortByOrder.Descending;
    updateQueryState({
      field: sortField,
      order: sortOrderDirection,
      page: page.toString(),
    });
  };

  return {
    sortField,
    sortDirection: order,
    onSortSelect,
    onSortOrderChange,
  };
}
